import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { AVAILABLE_FONTS, type Block } from "@/types/proposals";
import { AlignLeft, AlignCenter, AlignRight, AlignJustify, Square, Circle, Minus, Triangle } from "lucide-react";

interface StyleEditorProps {
  block: Block | null;
  onUpdateBlock: (blockId: string, updates: Partial<Block>) => void;
}

export function StyleEditor({ block, onUpdateBlock }: StyleEditorProps) {
  if (!block) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Style Editor</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground text-center py-8">
          <p>👈 Select a block to edit its style</p>
          <p className="text-xs mt-2">Click any block on the canvas</p>
        </CardContent>
      </Card>
    );
  }

  const style = block.style || {};
  const position = block.position;
  const content = block.content || {};

  const updateStyle = (updates: Partial<Block['style']>) => {
    onUpdateBlock(block.id, {
      style: { ...style, ...updates }
    });
  };

  const updatePosition = (updates: Partial<Block['position']>) => {
    onUpdateBlock(block.id, {
      position: { ...position, ...updates }
    });
  };

  const updateContent = (updates: any) => {
    onUpdateBlock(block.id, {
      content: { ...content, ...updates }
    });
  };

  const isShapeBlock = block.type === 'shape';
  const isIconBlock = block.type === 'icon';
  const isImageBlock = block.type === 'image' || block.type === 'dynamic_image';

  return (
    <TooltipProvider delayDuration={300}>
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Style Editor</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="appearance" className="w-full">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="appearance" className="text-xs">Appearance</TabsTrigger>
              <TabsTrigger value="text" className="text-xs">Text</TabsTrigger>
              <TabsTrigger value="position" className="text-xs">Position</TabsTrigger>
            </TabsList>

            {/* Appearance Tab (merged Style + Effects) */}
            <TabsContent value="appearance" className="space-y-4 mt-4">
              {/* Cover Block Content Editor */}
              {block.type === 'cover' && (
                <>
                  <div>
                    <Label className="text-xs font-semibold">Company Info</Label>
                    <div className="space-y-2 mt-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">Company Name</Label>
                        <Input
                          value={content.companyName || ''}
                          onChange={(e) => updateContent({ companyName: e.target.value })}
                          placeholder="Your Company Name"
                          className="h-8 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Tagline</Label>
                        <Input
                          value={content.companyTagline || ''}
                          onChange={(e) => updateContent({ companyTagline: e.target.value })}
                          placeholder="Professional Roofing Services"
                          className="h-8 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Company Logo URL</Label>
                        <Input
                          value={content.companyLogo || ''}
                          onChange={(e) => updateContent({ companyLogo: e.target.value })}
                          placeholder="https://..."
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs font-semibold">Service Details</Label>
                    <div className="space-y-2 mt-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">Service Type</Label>
                        <Input
                          value={content.serviceType || ''}
                          onChange={(e) => updateContent({ serviceType: e.target.value })}
                          placeholder="Roof Replacement"
                          className="h-8 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Date</Label>
                        <Input
                          value={content.date || ''}
                          onChange={(e) => updateContent({ date: e.target.value })}
                          placeholder="December 1, 2025"
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs font-semibold">Customer Info</Label>
                    <div className="space-y-2 mt-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">Customer Name</Label>
                        <Input
                          value={content.customerName || ''}
                          onChange={(e) => updateContent({ customerName: e.target.value })}
                          placeholder="Homeowner Name"
                          className="h-8 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Address</Label>
                        <Input
                          value={content.customerAddress || ''}
                          onChange={(e) => updateContent({ customerAddress: e.target.value })}
                          placeholder="123 Main Street"
                          className="h-8 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">City, State ZIP</Label>
                        <Input
                          value={content.customerCityStateZip || ''}
                          onChange={(e) => updateContent({ customerCityStateZip: e.target.value })}
                          placeholder="City, ST 12345"
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs font-semibold">Property Photo URL</Label>
                    <Input
                      value={content.propertyPhoto || ''}
                      onChange={(e) => updateContent({ propertyPhoto: e.target.value })}
                      placeholder="https://..."
                      className="h-8 text-xs mt-2"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-semibold">Brand Color</Label>
                    <p className="text-[10px] text-muted-foreground">Controls the accent color throughout your cover page</p>
                    <div className="flex items-center gap-3">
                      <Input
                        type="color"
                        value={content.brandColor || '#2BC5B4'}
                        onChange={(e) => updateContent({ brandColor: e.target.value })}
                        className="h-12 w-20 cursor-pointer"
                      />
                      <Input
                        type="text"
                        value={content.brandColor || '#2BC5B4'}
                        onChange={(e) => updateContent({ brandColor: e.target.value })}
                        placeholder="#2BC5B4"
                        className="h-8 text-xs font-mono flex-1"
                      />
                    </div>
                    <div className="grid grid-cols-6 gap-2">
                      {['#2BC5B4', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444', '#10B981'].map((color) => (
                        <button
                          key={color}
                          onClick={() => updateContent({ brandColor: color })}
                          className="h-8 rounded border-2 border-border hover:border-primary transition-colors"
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs font-semibold">Contact Info</Label>
                    <div className="space-y-2 mt-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">Sales Rep Name</Label>
                        <Input
                          value={content.salesRepName || ''}
                          onChange={(e) => updateContent({ salesRepName: e.target.value })}
                          placeholder="John Smith"
                          className="h-8 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Email</Label>
                        <Input
                          value={content.companyEmail || ''}
                          onChange={(e) => updateContent({ companyEmail: e.target.value })}
                          placeholder="info@company.com"
                          className="h-8 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Phone</Label>
                        <Input
                          value={content.companyPhone || ''}
                          onChange={(e) => updateContent({ companyPhone: e.target.value })}
                          placeholder="(555) 123-4567"
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {isShapeBlock && (
                <>
                  <div>
                    <Label className="text-xs">Shape Type</Label>
                    <div className="grid grid-cols-5 gap-2 mt-2">
                      {[
                        { type: 'rectangle', icon: Square, label: 'Rectangle' },
                        { type: 'circle', icon: Circle, label: 'Circle' },
                        { type: 'line', icon: Minus, label: 'Line' },
                        { type: 'triangle', icon: Triangle, label: 'Triangle' },
                      ].map(({ type, icon: Icon, label }) => (
                        <Tooltip key={type}>
                          <TooltipTrigger asChild>
                            <Button
                              variant={content.shapeType === type ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => updateContent({ shapeType: type })}
                              className="h-10 p-0"
                            >
                              <Icon className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{label}</TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Fill Color</Label>
                    <Input
                      type="color"
                      value={content.fillColor || '#3B82F6'}
                      onChange={(e) => updateContent({ fillColor: e.target.value })}
                      className="h-10 mt-2"
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Stroke Color</Label>
                    <Input
                      type="color"
                      value={content.strokeColor || '#1E40AF'}
                      onChange={(e) => updateContent({ strokeColor: e.target.value })}
                      className="h-10 mt-2"
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Stroke Width: {content.strokeWidth || 2}px</Label>
                    <Slider
                      value={[content.strokeWidth || 2]}
                      onValueChange={([value]) => updateContent({ strokeWidth: value })}
                      min={0}
                      max={10}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Rotation: {content.rotation || 0}°</Label>
                    <Slider
                      value={[content.rotation || 0]}
                      onValueChange={([value]) => updateContent({ rotation: value })}
                      min={-180}
                      max={180}
                      className="mt-2"
                    />
                  </div>
                </>
              )}

              {isIconBlock && (
                <>
                  <div>
                    <Label className="text-xs">Icon Color</Label>
                    <Input
                      type="color"
                      value={style.textColor || '#000000'}
                      onChange={(e) => updateStyle({ textColor: e.target.value })}
                      className="h-10 mt-2"
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Stroke Width: {content.strokeWidth || 2}px</Label>
                    <Slider
                      value={[content.strokeWidth || 2]}
                      onValueChange={([value]) => updateContent({ strokeWidth: value })}
                      min={1}
                      max={4}
                      step={0.5}
                      className="mt-2"
                    />
                  </div>
                </>
              )}

              {!isShapeBlock && !isIconBlock && (
                <>
                  <div>
                    <Label className="text-xs">Border</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <Input
                        type="number"
                        placeholder="Width"
                        value={style.borderWidth || 0}
                        onChange={(e) => updateStyle({ borderWidth: Number(e.target.value) })}
                        className="h-8"
                      />
                      <Input
                        type="color"
                        value={style.borderColor || '#000000'}
                        onChange={(e) => updateStyle({ borderColor: e.target.value })}
                        className="h-8"
                      />
                      <Select
                        value={style.borderStyle || 'solid'}
                        onValueChange={(value: any) => updateStyle({ borderStyle: value })}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="solid">Solid</SelectItem>
                          <SelectItem value="dashed">Dashed</SelectItem>
                          <SelectItem value="dotted">Dotted</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Corner Radius: {style.borderRadius || 0}px</Label>
                    <Slider
                      value={[style.borderRadius || 0]}
                      onValueChange={([value]) => updateStyle({ borderRadius: value })}
                      max={100}
                      className="mt-2"
                    />
                  </div>

                  {isImageBlock && (
                    <>
                      <div>
                        <Label className="text-xs">Image Fit</Label>
                        <Select
                          value={style.objectFit || 'cover'}
                          onValueChange={(value: any) => updateStyle({ objectFit: value })}
                        >
                          <SelectTrigger className="h-8 mt-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cover">Cover (fill & crop)</SelectItem>
                            <SelectItem value="contain">Contain (fit inside)</SelectItem>
                            <SelectItem value="fill">Fill (stretch)</SelectItem>
                            <SelectItem value="none">None (original size)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center gap-2">
                        <Switch
                          checked={style.clipPath === 'circle(50%)'}
                          onCheckedChange={(checked) =>
                            updateStyle({ clipPath: checked ? 'circle(50%)' : undefined })
                          }
                        />
                        <Label className="text-xs">Make Circular</Label>
                      </div>
                    </>
                  )}

                  <div>
                    <Label className="text-xs">Background</Label>
                    <Input
                      type="color"
                      value={style.backgroundColor || '#ffffff'}
                      onChange={(e) => updateStyle({ backgroundColor: e.target.value })}
                      className="h-8 mt-2"
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Padding: {style.padding || 0}px</Label>
                    <Slider
                      value={[style.padding || 0]}
                      onValueChange={([value]) => updateStyle({ padding: value })}
                      max={64}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Shadow</Label>
                    <Select
                      value={style.boxShadow || 'none'}
                      onValueChange={(value: any) => updateStyle({ boxShadow: value })}
                    >
                      <SelectTrigger className="h-8 mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="sm">Small</SelectItem>
                        <SelectItem value="md">Medium</SelectItem>
                        <SelectItem value="lg">Large</SelectItem>
                        <SelectItem value="xl">Extra Large</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs">Opacity: {Math.round((style.opacity || 1) * 100)}%</Label>
                    <Slider
                      value={[(style.opacity || 1) * 100]}
                      onValueChange={([value]) => updateStyle({ opacity: value / 100 })}
                      max={100}
                      className="mt-2"
                    />
                  </div>
                </>
              )}
            </TabsContent>

            {/* Text Tab */}
            <TabsContent value="text" className="space-y-4 mt-4">
              <div>
                <Label className="text-xs">Font Family</Label>
                <Select
                  value={style.fontFamily || AVAILABLE_FONTS[0].family}
                  onValueChange={(value) => updateStyle({ fontFamily: value })}
                >
                  <SelectTrigger className="h-8 mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_FONTS.map(font => (
                      <SelectItem key={font.name} value={font.family} style={{ fontFamily: font.family }}>
                        {font.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs">Font Size: {style.fontSize || 16}px</Label>
                <Slider
                  value={[style.fontSize || 16]}
                  onValueChange={([value]) => updateStyle({ fontSize: value })}
                  min={10}
                  max={72}
                  className="mt-2"
                />
              </div>

              <div>
                <Label className="text-xs">Font Weight</Label>
                <Select
                  value={style.fontWeight || 'normal'}
                  onValueChange={(value: any) => updateStyle({ fontWeight: value })}
                >
                  <SelectTrigger className="h-8 mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="semibold">Semi Bold</SelectItem>
                    <SelectItem value="bold">Bold</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs">Text Color</Label>
                <Input
                  type="color"
                  value={style.textColor || '#000000'}
                  onChange={(e) => updateStyle({ textColor: e.target.value })}
                  className="h-8 mt-2"
                />
              </div>

              <div>
                <Label className="text-xs">Alignment</Label>
                <div className="flex gap-2 mt-2">
                  <Button
                    size="sm"
                    variant={style.textAlign === 'left' ? 'default' : 'outline'}
                    onClick={() => updateStyle({ textAlign: 'left' })}
                    className="flex-1"
                  >
                    <AlignLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant={style.textAlign === 'center' ? 'default' : 'outline'}
                    onClick={() => updateStyle({ textAlign: 'center' })}
                    className="flex-1"
                  >
                    <AlignCenter className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant={style.textAlign === 'right' ? 'default' : 'outline'}
                    onClick={() => updateStyle({ textAlign: 'right' })}
                    className="flex-1"
                  >
                    <AlignRight className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant={style.textAlign === 'justify' ? 'default' : 'outline'}
                    onClick={() => updateStyle({ textAlign: 'justify' })}
                    className="flex-1"
                  >
                    <AlignJustify className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-xs">Line Height: {style.lineHeight || 1.5}</Label>
                <Slider
                  value={[style.lineHeight || 1.5]}
                  onValueChange={([value]) => updateStyle({ lineHeight: value })}
                  min={1}
                  max={3}
                  step={0.1}
                  className="mt-2"
                />
              </div>
            </TabsContent>

            {/* Position Tab */}
            <TabsContent value="position" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">X</Label>
                  <Input
                    type="number"
                    value={position.x}
                    onChange={(e) => updatePosition({ x: Number(e.target.value) })}
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs">Y</Label>
                  <Input
                    type="number"
                    value={position.y}
                    onChange={(e) => updatePosition({ y: Number(e.target.value) })}
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs">Width</Label>
                  <Input
                    type="number"
                    value={position.width}
                    onChange={(e) => updatePosition({ width: Number(e.target.value) })}
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs">Height</Label>
                  <Input
                    type="number"
                    value={position.height === 'auto' ? '' : position.height}
                    onChange={(e) => updatePosition({ height: e.target.value ? Number(e.target.value) : 'auto' })}
                    placeholder="Auto"
                    className="h-8"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Layer (Z-Index): {position.zIndex}</Label>
                <Slider
                  value={[position.zIndex]}
                  onValueChange={([value]) => updatePosition({ zIndex: value })}
                  min={0}
                  max={999}
                  step={1}
                  className="mt-2"
                />
              </div>

              <div className="flex gap-2">
                <Button size="sm" onClick={() => updatePosition({ zIndex: 999 })} className="flex-1">
                  Front
                </Button>
                <Button size="sm" variant="outline" onClick={() => updatePosition({ zIndex: 0 })} className="flex-1">
                  Back
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          {/* Package Visibility Settings (for package_selector blocks) */}
          {block.type === 'package_selector' && (
            <div className="mt-6 pt-6 border-t space-y-4">
              <h3 className="font-semibold text-sm">Package Display Settings</h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="show-unit" className="text-xs">Show Unit</Label>
                  <Switch
                    id="show-unit"
                    checked={block.settings?.visibilitySettings?.showUnit ?? true}
                    onCheckedChange={(checked) => {
                      onUpdateBlock(block.id, {
                        settings: {
                          ...block.settings,
                          visibilitySettings: {
                            ...block.settings?.visibilitySettings,
                            showUnit: checked
                          }
                        }
                      });
                    }}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="show-quantity" className="text-xs">Show Quantity</Label>
                  <Switch
                    id="show-quantity"
                    checked={block.settings?.visibilitySettings?.showQuantity ?? true}
                    onCheckedChange={(checked) => {
                      onUpdateBlock(block.id, {
                        settings: {
                          ...block.settings,
                          visibilitySettings: {
                            ...block.settings?.visibilitySettings,
                            showQuantity: checked
                          }
                        }
                      });
                    }}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="show-prices" className="text-xs">Show Prices</Label>
                  <Switch
                    id="show-prices"
                    checked={block.settings?.visibilitySettings?.showUnitPrice ?? true}
                    onCheckedChange={(checked) => {
                      onUpdateBlock(block.id, {
                        settings: {
                          ...block.settings,
                          visibilitySettings: {
                            ...block.settings?.visibilitySettings,
                            showUnitPrice: checked,
                            layoutStyle: checked ? 'table' : 'stacked'
                          }
                        }
                      });
                    }}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="show-only-total" className="text-xs">Show Only Total</Label>
                  <Switch
                    id="show-only-total"
                    checked={block.settings?.visibilitySettings?.showOnlyTotal ?? false}
                    onCheckedChange={(checked) => {
                      onUpdateBlock(block.id, {
                        settings: {
                          ...block.settings,
                          visibilitySettings: {
                            ...block.settings?.visibilitySettings,
                            showOnlyTotal: checked
                          }
                        }
                      });
                    }}
                  />
                </div>

                <div>
                  <Label className="text-xs mb-2 block">Layout Style</Label>
                  <Select
                    value={block.settings?.visibilitySettings?.layoutStyle || 'table'}
                    onValueChange={(value: 'table' | 'stacked') => {
                      onUpdateBlock(block.id, {
                        settings: {
                          ...block.settings,
                          visibilitySettings: {
                            ...block.settings?.visibilitySettings,
                            layoutStyle: value
                          }
                        }
                      });
                    }}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="table">Table</SelectItem>
                      <SelectItem value="stacked">Stacked</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="pt-2">
                  <Label className="text-xs mb-2 block">Line Items</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Hide specific items (price stays locked)
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('openPackageItemsDialog', {
                        detail: { block }
                      }));
                    }}
                  >
                    Configure Line Items
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
